import {
  ComedianSourceCounts,
  DigestState,
  FollowSourceBreakdown,
  SourceBucketCount,
  SourceBucketWithDelta,
  TicketSourceBreakdown,
} from "../../models/comedian-weekly-digest";

import { Database } from "../../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { computeCohortMedianDirect } from "./cohort-median";
import { decideDigestState } from "./state-decision";
import { fetchFollowEventsBySource } from "./fetch-follow-events";
import { fetchFollowerCounts } from "./fetch-followers";
import { fetchTicketBuyersBySource } from "./fetch-ticket-buyers";
import { fetchVerifiedComedians } from "./fetch-verified-comedians";
import { logException } from "@punchup/sentry";

export interface BatchOptions {
  weekStart: string;
  weekEnd: string;
  priorWeekStart: string;
  priorWeekEnd: string;
  comedianId?: string;
  /**
   * Optional per-comedian filter. Called for each verified comedian AFTER
   * the cohort-median direct share has been computed (so the median is
   * always derived from the full population, not the filtered cohort).
   * Returning false skips row generation entirely — no `comedian_weekly_digest_runs`
   * row is written, and no downstream send / tip-generation step will see them.
   *
   * Used by the rollout gate (`comedian_weekly_digest_send` Statsig gate,
   * bucketed on `customIDs.stableID` with comedianId as the value); kept
   * as a generic predicate so the data package does not need to depend
   * on `@punchup/analytics`.
   */
  shouldIncludeComedian?: (comedianId: string) => Promise<boolean>;
}

export interface BatchResult {
  processed: number;
  suppressed: number;
  /**
   * Count of verified comedians excluded by `shouldIncludeComedian`. These
   * comedians did not get a digest row written and will not be emailed.
   */
  gated: number;
  errors: number;
  details: Array<{
    comedianId: string;
    comedianName: string;
    state: DigestState;
    error?: string;
  }>;
}

function sourceCountsToBreakdown(
  bucketMap: Map<string, Set<string>> | undefined
): { breakdown: FollowSourceBreakdown; total: number } {
  if (!bucketMap || bucketMap.size === 0) {
    return { breakdown: {}, total: 0 };
  }

  let total = 0;
  const counts: Array<[string, number]> = [];
  for (const [bucket, users] of bucketMap) {
    counts.push([bucket, users.size]);
    total += users.size;
  }

  const breakdown: FollowSourceBreakdown = {};
  for (const [bucket, count] of counts) {
    breakdown[bucket] = {
      count,
      pct: total > 0 ? (count / total) * 100 : 0,
    };
  }

  return { breakdown, total };
}

function computeTicketBreakdown(
  currentBuckets: Map<string, Set<string>> | undefined,
  priorBuckets: Map<string, Set<string>> | undefined
): {
  breakdown: TicketSourceBreakdown;
  currentTotal: number;
  priorTotal: number;
} {
  const { breakdown: currentBreakdown, total: currentTotal } =
    sourceCountsToBreakdown(currentBuckets);
  const { breakdown: priorBreakdown, total: priorTotal } =
    sourceCountsToBreakdown(priorBuckets);

  const allBuckets = new Set([
    ...Object.keys(currentBreakdown),
    ...Object.keys(priorBreakdown),
  ]);

  const breakdown: TicketSourceBreakdown = {};
  for (const bucket of allBuckets) {
    const cur = currentBreakdown[bucket] as SourceBucketCount | undefined;
    const prior = priorBreakdown[bucket] as SourceBucketCount | undefined;
    const pct = cur?.pct ?? 0;
    const priorPct = prior?.pct ?? 0;

    breakdown[bucket] = {
      count: cur?.count ?? 0,
      pct,
      prior_pct: priorPct,
      delta_pts: pct - priorPct,
    };
  }

  return { breakdown, currentTotal, priorTotal };
}

function topSource(
  breakdown: Record<string, SourceBucketCount>
): { name: string; pct: number } | null {
  let best: { name: string; pct: number } | null = null;
  for (const [name, { pct }] of Object.entries(breakdown)) {
    if (!best || pct > best.pct) {
      best = { name, pct };
    }
  }
  return best;
}

export async function runDigestBatch(
  supabase: SupabaseClient<Database>,
  options: BatchOptions
): Promise<BatchResult> {
  const {
    weekStart,
    weekEnd,
    priorWeekStart,
    priorWeekEnd,
    comedianId: onlyComedianId,
    shouldIncludeComedian,
  } = options;

  const verifiedComedians = await fetchVerifiedComedians(supabase);
  const verifiedIds = new Set(verifiedComedians.keys());

  const [
    ticketsCurrent,
    ticketsPrior,
    followsCurrent,
    followsPrior,
    followerCounts,
  ] = await Promise.all([
    fetchTicketBuyersBySource(supabase, weekStart, weekEnd),
    fetchTicketBuyersBySource(supabase, priorWeekStart, priorWeekEnd),
    fetchFollowEventsBySource(supabase, weekStart, weekEnd),
    fetchFollowEventsBySource(supabase, priorWeekStart, priorWeekEnd),
    fetchFollowerCounts(supabase, weekStart),
  ]);

  const directShares: Array<{
    comedianId: string;
    directShare: number;
    buyersTotal: number;
  }> = [];

  for (const comedianId of verifiedIds) {
    const currentBuckets = ticketsCurrent.get(comedianId);
    if (!currentBuckets) continue;

    const { currentTotal } = computeTicketBreakdown(currentBuckets, undefined);
    if (currentTotal === 0) continue;

    const directUsers = currentBuckets.get("Came straight to Punchup");
    const directCount = directUsers?.size ?? 0;
    const directShare = directCount / currentTotal;

    directShares.push({ comedianId, directShare, buyersTotal: currentTotal });
  }

  const cohortMedian = computeCohortMedianDirect(directShares);

  const result: BatchResult = {
    processed: 0,
    suppressed: 0,
    gated: 0,
    errors: 0,
    details: [],
  };

  const rows: Array<Record<string, unknown>> = [];

  for (const [comedianId, comedian] of verifiedComedians) {
    if (onlyComedianId && comedianId !== onlyComedianId) continue;

    if (shouldIncludeComedian) {
      const included = await shouldIncludeComedian(comedianId);
      if (!included) {
        result.gated++;
        continue;
      }
    }

    try {
      const followers = followerCounts.get(comedianId) ?? {
        total: 0,
        newThisWeek: 0,
      };

      const { breakdown: followBreakdown, total: followGross } =
        sourceCountsToBreakdown(followsCurrent.get(comedianId));
      const { total: followGrossPrior } = sourceCountsToBreakdown(
        followsPrior.get(comedianId)
      );

      const {
        breakdown: ticketBreakdown,
        currentTotal: buyersTotal,
        priorTotal: buyersPrior,
      } = computeTicketBreakdown(
        ticketsCurrent.get(comedianId),
        ticketsPrior.get(comedianId)
      );

      const directUsers = ticketsCurrent
        .get(comedianId)
        ?.get("Came straight to Punchup");
      const directShare =
        buyersTotal > 0 ? (directUsers?.size ?? 0) / buyersTotal : 0;

      const state = decideDigestState({
        buyersTotal,
        newFollowsGross: followGross,
        directShare,
      });

      const payload = {
        totalFollowers: followers.total,
        newFollowsNet: followers.newThisWeek,
        newFollowsGross: followGross,
        newFollowsGrossPrior: followGrossPrior,
        followSourceBreakdown: followBreakdown,
        buyersTotal,
        buyersPrior,
        ticketSourceBreakdown: ticketBreakdown,
        directShare,
        cohortMedianDirect: cohortMedian,
        aiTip: null,
        state,
      };

      rows.push({
        week_start: weekStart,
        comedian_id: comedianId,
        comedian_name: comedian.name,
        comedian_slug: comedian.slug,
        state,
        total_followers: followers.total,
        new_follows_net: followers.newThisWeek,
        new_follows_gross: followGross,
        new_follows_gross_prior: followGrossPrior,
        follow_source_breakdown: followBreakdown,
        buyers_total: buyersTotal,
        buyers_prior: buyersPrior,
        ticket_source_breakdown: ticketBreakdown,
        direct_share: directShare,
        cohort_median_direct: cohortMedian,
        ai_tip: null,
        ai_model: null,
        payload,
      });

      if (state === "suppressed") {
        result.suppressed++;
      }
      result.processed++;
      result.details.push({
        comedianId,
        comedianName: comedian.name,
        state,
      });
    } catch (err) {
      logException(err, {
        data: `[comedian-weekly-digest] Error processing comedian ${comedianId}`,
      });
      result.errors++;
      result.details.push({
        comedianId,
        comedianName: comedian.name,
        state: "suppressed",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  if (rows.length > 0) {
    await upsertDigestRows(supabase, rows);
  }

  return result;
}

const UPSERT_BATCH_SIZE = 100;

async function upsertDigestRows(
  supabase: SupabaseClient<Database>,
  rows: Array<Record<string, unknown>>
) {
  for (let i = 0; i < rows.length; i += UPSERT_BATCH_SIZE) {
    const batch = rows.slice(i, i + UPSERT_BATCH_SIZE);

    const { error } = await supabase
      .from("comedian_weekly_digest_runs")
      .upsert(
        batch as Database["public"]["Tables"]["comedian_weekly_digest_runs"]["Insert"][],
        {
          onConflict: "week_start,comedian_id",
          ignoreDuplicates: false,
        }
      );

    if (error) {
      logException(error, {
        data: `[comedian-weekly-digest] Failed to upsert digest rows (batch ${i / UPSERT_BATCH_SIZE + 1})`,
      });
      throw error;
    }
  }
}
